import { Text, View, Image, Platform, FlatList, Alert, ScrollView } from 'react-native';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as Animatable from 'react-native-animatable';
import { useDispatch, useSelector } from 'react-redux';
import apis, { refreshGoogleAccess } from '@api/api';
import LottieView from '@utils/lottieAnimation';
import { useLottieAnim } from '@hooks/lottieHook';
import { SECOND_MENU_TOGGLE, SHOW_MAIL_MODAL, SHOW_TOOLBAR, UPDATE_MAIL_COUNT, SET_MAIL_RETRIEVAL_ERROR, CLEAR_MAIL_RETRIEVAL_ERROR, SHOW_AUTH_MODAL, MAIL_AUTHENTICATED, UPDATE_USER } from '@reduxLocal/actions/types';
// import GoogleAuthenticator from '../components/GoogleMail';
import { Pressable } from 'react-native';
import { DefaultRootState } from '@reduxLocal/persistState';
import DefaultScreen from '@components/Global/DefaultScreenView';
import Button from '@components/UI/Button/Button';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import { useFocusEffect } from '@react-navigation/native';
import getEmailCount from '@utils/getEmailCount';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { H2, H5, H4, Separator, XStack, YStack, SizableText, Dialog } from 'tamagui';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { Card } from '@constants/Themed';
import UsageStatMonitor from '../SettingsScreens/Components/UsageStatMonitor';

const MailLoginScreen = () => {
  const animation = useLottieAnim();
  const messageCount = useSelector((state: DefaultRootState) => state.app.mail?.mailCount)
  const mailRetrievalError = useSelector((state: DefaultRootState) => state.app.mail?.retrievalError)
  const appMailAuthenticated = useSelector((state: DefaultRootState) => state.app.mail?.mailAuthenticated);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const userMailAuth = useSelector((state: DefaultRootState) => state.promiseData.data.userData.authentications?.mail?.mailAuthenticated);
  const userMailType = useSelector((state: DefaultRootState) => state.promiseData.data.userData.authentications?.mail?.mailType);
  const googleObject = googleAuths();
  const theme = themeSelector();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const gMail = googleAuths()?.scopes?.includes('https://www.googleapis.com/auth/gmail.modify');
  const googleAuthEmail = googleAuths()?.email;
  
  // Track previous auth state to detect changes
  const prevAuthStateRef = useRef<{ mailAuthenticated: boolean; userMailType: string | undefined }>({
    mailAuthenticated: false,
    userMailType: undefined
  });

  // Track last error timestamp to prevent excessive retries
  const lastErrorTimestampRef = useRef<number>(0);
  const lastErrorStatusRef = useRef<number | null>(null);
  const RETRY_COOLDOWN_MS = 30000; // 30 seconds cooldown after errors
  const SERVER_ERROR_COOLDOWN_MS = 60000; // 60 seconds cooldown for 500 errors

  const { data: mailboxes, error, isFetching, refetch, isLoading } = useQuery({
    queryKey: ['mailboxes'],
    queryFn: apis.getMailBoxes,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: currentCompany ? true : false
  });

  const [selectedMailbox, setSelectedMailbox] = useState(null);

  // Set selected mailbox when mailboxes load
  useEffect(() => {
    if (mailboxes && Array.isArray(mailboxes) && mailboxes.length > 0) {
      const defaultMailbox = mailboxes.find(m => m.isDefault) || mailboxes[0];
      setSelectedMailbox(defaultMailbox);
    }
  }, [mailboxes]);

  const handleSetDefaultMailbox = async (mailboxId: string) => {
    try {
      await apis.setDefaultMailbox(mailboxId);
      await refetch();
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: 'Default mailbox updated successfully',
          type: 'success'
        }
      });
    } catch (error) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: error?.response?.data?.error || 'Failed to update default mailbox',
          type: 'error'
        }
      });
    }
  };

  const handleDeleteMailbox = async (mailboxId: string, email: string) => {
    // Show confirmation dialog
    Alert.alert(
      'Delete Mailbox',
      `Are you sure you want to delete ${email}? This action cannot be undone.`,
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await apis.deleteMailbox(mailboxId);
              await refetch();
              dispatch({
                type: SHOW_AUTH_MODAL,
                data: {
                  content: `Mailbox ${email} deleted successfully`,
                  type: 'success'
                }
              });
            } catch (error) {
              dispatch({
                type: SHOW_AUTH_MODAL,
                data: {
                  content: error?.response?.data?.error || 'Failed to delete mailbox',
                  type: 'error'
                }
              });
            }
          }
        }
      ]
    );
  };

  // Determine mail authentication status based on context
  // Check both app.mail.mailAuthenticated (set by MAIL_AUTHENTICATED action) 
  // and userData.authentications.mail.mailAuthenticated (set by UPDATE_USER action)
  const mailAuthenticated = React.useMemo(() => {
    if (currentCompany) {
      // For company context, check mailboxes instead
      return false;
    }
    // Check both state paths for comprehensive auth status
    const isAuthenticated = 
      appMailAuthenticated === true || 
      (userMailAuth === true && (userMailType === 'google' || userMailType === 'manual'));
    return isAuthenticated;
  }, [currentCompany, appMailAuthenticated, userMailAuth, userMailType]);

  // Determine if account is Google Workspace or standard Gmail
  const isGoogleWorkspace = React.useMemo(() => {
    if (!googleAuthEmail || !mailAuthenticated || userMailType !== 'google') {
      return null; // Not applicable or not Google account
    }
    // Standard Gmail accounts end with @gmail.com or @googlemail.com
    // Google Workspace accounts have custom domains
    const emailDomain = googleAuthEmail.toLowerCase();
    return !(emailDomain.endsWith('@gmail.com') || emailDomain.endsWith('@googlemail.com'));
  }, [googleAuthEmail, mailAuthenticated, userMailType]);

  // Get email limit based on account type
  const emailLimit = React.useMemo(() => {
    if (!mailAuthenticated || userMailType !== 'google') {
      return null;
    }
    return isGoogleWorkspace ? 2000 : 500;
  }, [mailAuthenticated, userMailType, isGoogleWorkspace]);

  // Modal state for email limits info
  const [emailLimitsModalOpen, setEmailLimitsModalOpen] = useState(false);

  // Track if a mail count request is in progress to prevent duplicates
  const mailCountRequestInProgressRef = useRef(false);

  // Memoize handleGetMail to prevent unnecessary re-renders
  // Must be defined before useFocusEffect to avoid initialization error
  const handleGetMail = useCallback(async () => {
    // Prevent duplicate calls
    if (!googleObject?.googleId || !googleObject?.accessToken) {
      return;
    }

    // Prevent concurrent requests
    if (mailCountRequestInProgressRef.current) {
      return;
    }

    // Check if we should skip retry due to recent error
    const now = Date.now();
    const timeSinceLastError = now - lastErrorTimestampRef.current;
    const cooldownPeriod = lastErrorStatusRef.current === 500
      ? SERVER_ERROR_COOLDOWN_MS
      : RETRY_COOLDOWN_MS;

    if (lastErrorTimestampRef.current > 0 && timeSinceLastError < cooldownPeriod) {
      // Skip retry if we're still in cooldown period
      return;
    }

    mailCountRequestInProgressRef.current = true;

    try {
      const res = await apis.getGmailCount(googleObject.googleId, googleObject.accessToken);
      if (res?.data?.messagesUnread !== undefined) {
        dispatch({
          type: UPDATE_MAIL_COUNT,
          payload: res.data.messagesUnread
        });
        // Clear any previous retrieval errors on successful fetch
        if (mailRetrievalError) {
          dispatch({ type: CLEAR_MAIL_RETRIEVAL_ERROR });
        }
        // Reset error tracking on success
        lastErrorTimestampRef.current = 0;
        lastErrorStatusRef.current = null;
      }
    } catch (error: any) {
      console.error('Failed to get mail count:', error);
      
      const errorStatus = error?.response?.status;
      const errorTimestamp = Date.now();

      // Update error tracking
      lastErrorTimestampRef.current = errorTimestamp;
      lastErrorStatusRef.current = errorStatus || null;

      // Check if it's a duplicate request error
      if (error?.isDuplicate && error?.message === 'Duplicate request prevented') {
        dispatch({
          type: SET_MAIL_RETRIEVAL_ERROR,
          payload: {
            message: 'Failed to get mail count: Duplicate request prevented',
            isDuplicate: true,
            requiresReauth: false, // Duplicate requests don't require re-auth
            timestamp: errorTimestamp,
          },
        });
      } else if (errorStatus === 401 || errorStatus === 403) {
        // Authentication errors suggest re-authentication may be needed
        dispatch({
          type: SET_MAIL_RETRIEVAL_ERROR,
          payload: {
            message: `Failed to get mail count: ${errorStatus === 401 ? 'Authentication failed' : 'Access denied'}`,
            requiresReauth: true,
            timestamp: errorTimestamp,
          },
        });
      } else if (error?.message?.includes('token') || error?.message?.includes('auth')) {
        // Token or auth related errors
        dispatch({
          type: SET_MAIL_RETRIEVAL_ERROR,
          payload: {
            message: `Failed to get mail count: ${error?.message || 'Authentication error'}`,
            requiresReauth: true,
            timestamp: errorTimestamp,
          },
        });
      } else if (errorStatus === 500) {
        // Server errors - don't retry aggressively
        dispatch({
          type: SET_MAIL_RETRIEVAL_ERROR,
          payload: {
            message: 'Failed to get mail count: Server error. Please try again later.',
            requiresReauth: false,
            timestamp: errorTimestamp,
          },
        });
      } else {
        // Other errors (network, timeout, etc.)
        dispatch({
          type: SET_MAIL_RETRIEVAL_ERROR,
          payload: {
            message: `Failed to get mail count: ${error?.message || 'Network error. Please check your connection.'}`,
            requiresReauth: false,
            timestamp: errorTimestamp,
          },
        });
      }
      // The interceptor will handle token refresh and retry
      // If it still fails after retry, the error modal will be shown by the interceptor
    } finally {
      mailCountRequestInProgressRef.current = false;
    }
  }, [googleObject?.googleId, googleObject?.accessToken, dispatch]);

  useFocusEffect(
    React.useCallback(() => {
      dispatch({ type: SECOND_MENU_TOGGLE });

      // Fetch email count for user mail if authenticated
      if (!currentCompany && mailAuthenticated && userMailType === 'google' && gMail) {
        // Check if we should skip retry due to recent error
        const now = Date.now();
        const timeSinceLastError = now - lastErrorTimestampRef.current;
        const cooldownPeriod = lastErrorStatusRef.current === 500
          ? SERVER_ERROR_COOLDOWN_MS
          : RETRY_COOLDOWN_MS;

        // Only retry if no recent error or cooldown has passed
        if (lastErrorTimestampRef.current === 0 || timeSinceLastError >= cooldownPeriod) {
          // Add a small delay to ensure state is stable
          const timeoutId = setTimeout(() => {
            handleGetMail();
          }, 200);
          return () => clearTimeout(timeoutId);
        }
      }

      // Refetch mailboxes when in company context
      if (currentCompany) {
        // Invalidate and refetch to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
        refetch();
      }
    }, [currentCompany, mailAuthenticated, userMailType, gMail, refetch, dispatch, queryClient, handleGetMail])
  );

  // Handle auth state changes - refetch data when authentication status changes
  useEffect(() => {
    const prevState = prevAuthStateRef.current;
    const currentState = {
      mailAuthenticated,
      userMailType
    };

    // Detect if authentication state actually changed
    const authStateChanged = 
      prevState.mailAuthenticated !== currentState.mailAuthenticated ||
      prevState.userMailType !== currentState.userMailType;

    let timeoutId: NodeJS.Timeout | null = null;

    if (authStateChanged) {
      // If user just authenticated, invalidate and refetch relevant queries
      if (currentState.mailAuthenticated && !prevState.mailAuthenticated) {
        // Invalidate mailboxes query to refetch after auth
        if (currentCompany) {
          queryClient.invalidateQueries({ queryKey: ['mailboxes'] });
        }

        // Fetch email count for user mail if authenticated
        // Only fetch when user just authenticated (not on every render)
        if (userMailType === 'google' && gMail && !currentCompany) {
          // Reset error tracking when authentication succeeds
          lastErrorTimestampRef.current = 0;
          lastErrorStatusRef.current = null;

        // Use a small delay to ensure state is fully updated
          timeoutId = setTimeout(() => {
            handleGetMail();
            // Clear any previous errors when authentication succeeds
            if (mailRetrievalError) {
              dispatch({ type: CLEAR_MAIL_RETRIEVAL_ERROR });
            }
          }, 100);
        }
      }

      // Update ref for next comparison
      prevAuthStateRef.current = currentState;
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [mailAuthenticated, userMailType, gMail, currentCompany, queryClient, mailRetrievalError, dispatch, handleGetMail]);

  // Show modal for company context if no mailboxes and not loading
  useEffect(() => {
    if (currentCompany && !isLoading && !isFetching && (!mailboxes || mailboxes.length === 0) && !error) {
      // Don't auto-show modal, let user click button instead
    }
  }, [currentCompany, mailboxes, isLoading, isFetching, error]);


  return (
    <DefaultScreen noMargin screenTitle={'Mail'} scrollDisabled={false} containerStyle={{ paddingBottom: 40 }}>
      <YStack gap={24} paddingHorizontal={20} paddingTop={currentCompany ? 16 : 0} paddingBottom={40}>
        {/* SHOW MAIL MODAL IF USER HAS NOT ALREADY AUTHENTICATED MAIL LOGIN*/}
        {currentCompany &&
          <Animatable.View animation="fadeInDown" duration={800} style={[styles.modernSection, { marginBottom: 24 }]}>
            <XStack alignItems="center" justifyContent="space-between" paddingHorizontal={10} marginBottom={16}>
              <H4 fontWeight={"bold"} style={styles.modernSectionTitle}>
                Company Mailboxes
              </H4>
              <Pressable
                onPress={() => dispatch({ type: SHOW_MAIL_MODAL })}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 8,
                  backgroundColor: theme ? '#3b82f6' : '#2563eb',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <AntDesign name="plus" size={16} color="white" />
                <SizableText size="$3" color="white" fontWeight="600">
                  Add
                </SizableText>
              </Pressable>
            </XStack>
            <Separator marginVertical={16} />
            {isLoading || isFetching ? (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <SizableText size="$3" color={getModernTextColor(theme, 'secondary')}>
                  Loading mailboxes...
                </SizableText>
              </View>
            ) : mailboxes && Array.isArray(mailboxes) && mailboxes.length > 0 ? (
                <FlatList
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  data={mailboxes}
                  contentContainerStyle={[styles.modernHorizontalList, { paddingRight: 20 }]}
                  renderItem={({ item, index }) => (
                    <Animatable.View
                      animation="slideInRight"
                      duration={600}
                      delay={index * 100}
                      style={{ marginRight: 12 }}
                    >
                      <Card
                        backgroundColor={theme ? '#ffffff' : '#1e293b'}
                        padding={16}
                        borderRadius={12}
                        borderWidth={item.isDefault ? 2 : 1}
                        borderColor={item.isDefault
                          ? (theme ? '#3b82f6' : '#60a5fa')
                          : (theme ? '#e5e7eb' : '#475569')
                        }
                        style={{
                          minWidth: 200,
                          maxWidth: 250,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: 0.1,
                          shadowRadius: 4,
                          elevation: 3,
                        }}
                      >
                        <YStack gap={12}>
                          <XStack alignItems="center" justifyContent="space-between">
                            <MaterialIcons
                              name={item.mailType === 'google' ? 'mail' : item.mailType === 'manual' ? 'settings' : 'email'}
                              size={24}
                              color={item.isDefault
                                ? (theme ? '#3b82f6' : '#60a5fa')
                                : getModernTextColor(theme, 'secondary')
                              }
                            />
                            {item.isDefault && (
                              <View style={{
                                backgroundColor: theme ? '#eff6ff' : '#1e3a5f',
                                paddingHorizontal: 8,
                                paddingVertical: 4,
                                borderRadius: 6,
                              }}>
                                <SizableText size="$1" color={theme ? '#3b82f6' : '#60a5fa'} fontWeight="700">
                                  DEFAULT
                                </SizableText>
                              </View>
                            )}
                          </XStack>

                          <YStack gap={4}>
                            <SizableText
                              size="$4"
                              fontWeight="600"
                              style={{
                                color: getModernTextColor(theme),
                                marginBottom: 4,
                              }}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {item.email}
                            </SizableText>
                            <SizableText
                              size="$2"
                              color={getModernTextColor(theme, 'secondary')}
                            >
                              {item.mailType === 'google' ? 'Google Account' :
                                item.mailType === 'manual' ? 'SMTP Account' :
                                  item.mailType || 'Unknown'}
                            </SizableText>
                          </YStack>

                          <Separator marginVertical={4} />

                          <XStack gap={8} justifyContent="space-between">
                            {!item.isDefault && (
                              <Pressable
                                onPress={() => handleSetDefaultMailbox(item._id)}
                                style={{
                                  flex: 1,
                                  paddingVertical: 8,
                                  paddingHorizontal: 12,
                                  borderRadius: 8,
                                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                                  alignItems: 'center',
                                }}
                              >
                                <SizableText size="$2" color={getModernTextColor(theme)} fontWeight="600">
                                  Set Default
                                </SizableText>
                              </Pressable>
                            )}
                            <Pressable
                              onPress={() => handleDeleteMailbox(item._id, item.email)}
                              style={{
                                padding: 8,
                                borderRadius: 8,
                                backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                              }}
                            >
                              <MaterialIcons
                                name="delete"
                                size={18}
                                color={theme ? '#dc2626' : '#fca5a5'}
                              />
                            </Pressable>
                          </XStack>

                          <Pressable
                            onPress={() => navigation.navigate('Main' as never)}
                            style={{
                              paddingVertical: 10,
                              paddingHorizontal: 12,
                              borderRadius: 8,
                              backgroundColor: theme ? '#3b82f6' : '#2563eb',
                              alignItems: 'center',
                            }}
                          >
                            <SizableText size="$3" color="white" fontWeight="600">
                              Open Mailbox
                            </SizableText>
                          </Pressable>
                        </YStack>
                      </Card>
                    </Animatable.View>
                  )}
                />
            ) : (
              <View style={{ padding: 20, alignItems: 'center' }}>
                <SizableText size="$3" color={getModernTextColor(theme, 'secondary')} marginBottom={16}>
                  No mailboxes configured
                </SizableText>
                <Button
                  buttonText="Add Email Account"
                  icon="mail"
                  onPress={() => dispatch({ type: SHOW_MAIL_MODAL })}
                  customStyle={{
                    ...styles.modernButton,
                    backgroundColor: theme ? '#3b82f6' : '#4f46e5',
                    minWidth: Platform.select({ web: 200, default: '100%' }),
                  }}
                />
              </View>
            )}
            <Separator marginVertical={16} />
          </Animatable.View>
        }

        <Animatable.View animation="fadeInRight" duration={800} delay={300} style={{ flex: 1 }}>
          <H4 paddingHorizontal={10} fontWeight={"bold"} style={styles.modernSectionTitle}>
            Your Mailbox
          </H4>
          <View style={[styles.modernGrid, { marginTop: 12, marginBottom: 20 }]}>
            <Animatable.View animation="zoomIn" duration={600} delay={400}>
              <Pressable
                disabled={!mailAuthenticated}
                onPress={() => {
                  if (mailAuthenticated) {
                    navigation.navigate('Main' as never);
                  }
                }}
                style={[
                  getModernCardStyle(theme, 'small'),
                  { opacity: mailAuthenticated ? 1 : 0.7 }
                ]}
              >
                <MaterialIcons 
                  name="message" 
                  size={24} 
                  color={theme ? '#3b82f6' : '#60a5fa'} 
                  style={styles.modernCardIcon}
                />
                <SizableText 
                  size="$4" 
                  fontWeight="600" 
                  style={[
                    styles.modernCardTitle,
                    { color: getModernTextColor(theme) }
                  ]}
                >
                  {t("smMessages")}
                </SizableText>
              </Pressable>
            </Animatable.View>
            
            <Animatable.View animation="zoomIn" duration={600} delay={500}>
              <Pressable 
                disabled={!mailAuthenticated} 
                onPress={() => {
                  if (mailAuthenticated) {
                    navigation.navigate(
                      'Mail' as never, { screen: 'Main' as never } as never
                    );
                  }
                }} 
                style={[
                  getModernCardStyle(theme, 'small'),
                  { opacity: mailAuthenticated ? 1 : 0.7 }
                ]}
              >
                <MaterialIcons 
                  name="mark-email-unread" 
                  size={24} 
                  color={theme ? '#ef4444' : '#f87171'} 
                  style={styles.modernCardIcon}
                />
                <SizableText 
                  size="$4" 
                  fontWeight="600" 
                  style={[
                    styles.modernCardTitle,
                    { color: getModernTextColor(theme) }
                  ]}
                >
                  {t("unreadMail")}
                </SizableText>
                <SizableText 
                  size="$8" 
                  fontWeight="700" 
                  style={[
                    styles.modernStatsNumber,
                    { color: getModernTextColor(theme, 'muted') }
                  ]}
                >
                  {messageCount}
                </SizableText>
              </Pressable>
            </Animatable.View>
            
            <Animatable.View animation="zoomIn" duration={600} delay={600}>
              <Pressable
                disabled={!mailAuthenticated}
                onPress={() => {
                  if (mailAuthenticated) {
                    navigation.navigate('Main' as never);
                  }
                }}
                style={[
                  getModernCardStyle(theme, 'small'),
                  { opacity: mailAuthenticated ? 1 : 0.7 }
                ]}
              >
                <MaterialIcons 
                  name="priority-high" 
                  size={24} 
                  color={theme ? '#f59e0b' : '#fbbf24'} 
                  style={styles.modernCardIcon}
                />
                <SizableText 
                  size="$4" 
                  fontWeight="600" 
                  style={[
                    styles.modernCardTitle,
                    { color: getModernTextColor(theme) }
                  ]}
                >
                  {t("Important")}
                </SizableText>
              </Pressable>
            </Animatable.View>
          </View>

          {/* Email Limit Display for Gmail Users */}
          {mailAuthenticated && userMailType === 'google' && emailLimit !== null && (
            <Animatable.View
              animation="fadeInUp"
              duration={600}
              delay={650}
              style={{ paddingHorizontal: 0, marginTop: 8, marginBottom: 16 }}
            >
              <Card
                backgroundColor={theme ? '#eff6ff' : '#1e3a5f'}
                padding={16}
                borderRadius={12}
                borderWidth={1}
                borderColor={theme ? '#bfdbfe' : '#3b82f6'}
              >
                <YStack gap={12}>
                  <XStack alignItems="center" gap={12}>
                    <MaterialIcons
                      name="info-outline"
                      size={24}
                      color={theme ? '#3b82f6' : '#60a5fa'}
                    />
                    <YStack flex={1} gap={4}>
                      <SizableText
                        size="$5"
                        fontWeight="700"
                        color={theme ? '#1e40af' : '#93c5fd'}
                      >
                        Daily Email Limit
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? '#1e3a8a' : '#bfdbfe'}
                      >
                        {isGoogleWorkspace
                          ? 'Google Workspace Account'
                          : 'Standard Gmail Account'}
                      </SizableText>
                    </YStack>
                    <Pressable
                      onPress={() => setEmailLimitsModalOpen(true)}
                      style={{
                        padding: 8,
                        borderRadius: 8,
                        backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                      }}
                    >
                      <MaterialIcons
                        name="info"
                        size={20}
                        color={theme ? '#3b82f6' : '#60a5fa'}
                      />
                    </Pressable>
                  </XStack>
                  <Separator marginVertical={4} />
                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack flex={1}>
                      <SizableText
                        size="$3"
                        color={theme ? '#64748b' : '#94a3b8'}
                        marginBottom={4}
                      >
                        Maximum emails per day:
                      </SizableText>
                      <SizableText
                        size="$7"
                        fontWeight="700"
                        color={theme ? '#3b82f6' : '#60a5fa'}
                      >
                        {emailLimit.toLocaleString()}
                      </SizableText>
                    </YStack>
                    <MaterialIcons
                      name={isGoogleWorkspace ? "business" : "mail"}
                      size={32}
                      color={theme ? '#3b82f6' : '#60a5fa'}
                    />
                  </XStack>
                </YStack>
              </Card>
            </Animatable.View>
          )}

          <Animatable.View animation="slideInUp" duration={600} delay={700} style={{ paddingHorizontal: 0, marginTop: 8, marginBottom: 20 }}>
            <XStack
              gap={12}
              alignItems="center"
              flexWrap="wrap"
              justifyContent={Platform.select({ web: 'flex-start', default: 'stretch' })}
            >
              <Button
                buttonText={
                  currentCompany
                    ? "Add Email Account"
                    : mailAuthenticated
                      ? "Replace Email Account"
                      : "Connect Email Account"
                }
                icon="mail"
                onPress={() => dispatch({ type: SHOW_MAIL_MODAL })}
                customStyle={{
                  ...styles.modernButton,
                  backgroundColor: theme ? '#3b82f6' : '#4f46e5',
                  minWidth: Platform.select({ web: 220, default: '100%' }),
                  maxWidth: Platform.select({ web: 'auto', default: '100%' }),
                  alignSelf: Platform.select({ web: 'flex-start', default: 'stretch' }),
                  flex: Platform.select({ web: 0, default: 1 }),
                }}
              />
              <Pressable
                onPress={() => setEmailLimitsModalOpen(true)}
                style={{
                  padding: 12,
                  borderRadius: 12,
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  borderWidth: 1,
                  borderColor: theme ? '#e2e8f0' : '#475569',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 48,
                  minHeight: 48,
                }}
              >
                <MaterialIcons
                  name="info-outline"
                  size={24}
                  color={theme ? '#3b82f6' : '#60a5fa'}
                />
              </Pressable>
            </XStack>
          </Animatable.View>
          
          {mailRetrievalError && (
            <Animatable.View
              animation="fadeInDown"
              duration={400}
              style={{
                paddingHorizontal: 0,
                marginTop: 12,
                marginBottom: 12,
              }}
            >
              <Card
                backgroundColor={theme ? '#fef2f2' : '#7f1d1d'}
                padding={12}
                borderRadius={8}
                borderWidth={1}
                borderColor={theme ? '#fecaca' : '#991b1b'}
              >
                <YStack gap={8}>
                  <XStack alignItems="center" gap={12}>
                    <MaterialIcons
                      name="error-outline"
                      size={20}
                      color={theme ? '#dc2626' : '#fca5a5'}
                    />
                    <YStack flex={1} gap={4}>
                      <SizableText
                        size="$4"
                        fontWeight="600"
                        color={theme ? '#dc2626' : '#fca5a5'}
                      >
                        Mail Retrieval Failed
                      </SizableText>
                      <SizableText
                        size="$3"
                        color={theme ? '#991b1b' : '#fecaca'}
                      >
                        {mailRetrievalError.message || 'Failed to retrieve mail count'}
                      </SizableText>
                      {mailRetrievalError.requiresReauth && (
                        <SizableText
                          size="$2"
                          color={theme ? '#991b1b' : '#fecaca'}
                          marginTop={4}
                        >
                          You may need to re-authenticate your mail account.
                        </SizableText>
                      )}
                    </YStack>
                    <Pressable
                      onPress={() => dispatch({ type: CLEAR_MAIL_RETRIEVAL_ERROR })}
                      style={{
                        padding: 4,
                      }}
                    >
                      <AntDesign
                        name="close"
                        size={16}
                        color={theme ? '#dc2626' : '#fca5a5'}
                      />
                    </Pressable>
                  </XStack>
                  {mailRetrievalError.requiresReauth && mailAuthenticated && (
                    <XStack gap={8} justifyContent="flex-end">
                      <Button
                        buttonText="Re-authenticate Mail"
                        icon="refresh"
                        onPress={() => {
                          dispatch({ type: CLEAR_MAIL_RETRIEVAL_ERROR });
                          dispatch({ type: SHOW_MAIL_MODAL });
                        }}
                        customStyle={{
                          backgroundColor: theme ? '#dc2626' : '#991b1b',
                          paddingHorizontal: 16,
                          paddingVertical: 8,
                          minWidth: Platform.select({ web: 160, default: '100%' }),
                          maxWidth: Platform.select({ web: 'auto', default: '100%' }),
                        }}
                      />
                    </XStack>
                  )}
                </YStack>
              </Card>
            </Animatable.View>
          )}

          <View style={[styles.modernRow, {
            flexDirection: Platform.select({ web: 'row', default: 'column' }),
            alignItems: Platform.select({ web: 'center', default: 'stretch' }),
            gap: Platform.select({ web: 16, default: 20 }),
            marginTop: 20,
            marginBottom: 20,
          }]}>
            <Animatable.View
              animation="fadeInUp"
              duration={600}
              delay={800}
              style={{
                flex: Platform.select({ web: 2, default: 1 }),
                paddingVertical: 20,
                width: Platform.select({ web: 'auto', default: '100%' }),
              }}
            >
              <SizableText 
                size="$3" 
                style={[
                  styles.modernCautionText,
                  { color: getModernTextColor(theme, 'secondary') }
                ]}
              >
                {t("mailCaution1")}
                {'\n\n'}
                {t("mailCaution2")}
              </SizableText>
            </Animatable.View>
            
            <Animatable.View
              animation="bounceIn"
              duration={1000}
              delay={900}
              style={[
                styles.modernAnimationContainer,
                {
                  width: Platform.select({ web: 'auto', default: '100%' }),
                  alignItems: 'center',
                  justifyContent: 'center',
                }
              ]}
            >
              <LottieView 
                ref={animation} 
                style={{
                  width: Platform.select({ web: 200, default: '100%' }),
                  height: 180,
                  maxWidth: 200,
                  maxHeight: 180,
                }}
                source={require('@assets/animations/120976-e-mail-worldwide-delivery.json')} 
                autoPlay={true} 
                loop
              />
            </Animatable.View>
          </View>
        </Animatable.View>
      </YStack>

      {/* Email Limits Info Modal */}
      <Dialog key="emailLimits" open={emailLimitsModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: Platform.select({ web: 600, default: '90%' }),
                maxWidth: Platform.select({ web: 700, default: '95%' }),
                maxHeight: "75%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" gap="$3" justifyContent="space-between">
                <XStack alignItems="center" gap="$3" flex={1}>
                  <View style={{
                    backgroundColor: theme ? "#eff6ff" : "#1e3a5f",
                    borderRadius: 12,
                    padding: 10,
                  }}>
                    <MaterialIcons
                      name="info-outline"
                      size={24}
                      color={theme ? "#3b82f6" : "#60a5fa"}
                    />
                  </View>
                  <YStack flex={1}>
                    <Dialog.Title style={{
                      fontSize: 20,
                      fontWeight: '700',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4,
                      margin: 0,
                    }}>
                      Email Sending Limits
                    </Dialog.Title>
                    <Dialog.Description style={{
                      fontSize: 14,
                      color: theme ? '#64748b' : '#94a3b8',
                      fontWeight: '500',
                      margin: 0,
                    }}>
                      Daily sending limits by account type
                    </Dialog.Description>
                  </YStack>
                </XStack>
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => setEmailLimitsModalOpen(false)}
                    style={{
                      padding: 8,
                      borderRadius: 8,
                      backgroundColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <AntDesign
                      name="close"
                      size={20}
                      color={theme ? '#64748b' : '#94a3b8'}
                    />
                  </Pressable>
                </Dialog.Close>
              </XStack>
            </View>

            {/* Content */}
            <View style={{ padding: 24 }}>
              <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ gap: 20, paddingBottom: 100 }}>
                {/* Gmail Standard */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" gap="$3" marginBottom={12}>
                    <View style={{
                      backgroundColor: theme ? "#dbeafe" : "#1e3a5f",
                      borderRadius: 10,
                      padding: 8,
                    }}>
                      <MaterialIcons
                        name="mail"
                        size={24}
                        color={theme ? "#3b82f6" : "#60a5fa"}
                      />
                    </View>
                    <YStack flex={1}>
                      <SizableText style={{
                        fontSize: 18,
                        fontWeight: '700',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginBottom: 2,
                      }}>
                        Gmail (Standard)
                      </SizableText>
                      <SizableText style={{
                        fontSize: 12,
                        color: theme ? '#64748b' : '#94a3b8',
                      }}>
                        Personal Gmail accounts (@gmail.com)
                      </SizableText>
                    </YStack>
                    <View style={{
                      backgroundColor: theme ? "#eff6ff" : "#1e3a5f",
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                    }}>
                      <SizableText style={{
                        fontSize: 24,
                        fontWeight: '800',
                        color: theme ? "#3b82f6" : "#60a5fa",
                      }}>
                        500
                      </SizableText>
                      <SizableText style={{
                        fontSize: 11,
                        color: theme ? '#64748b' : '#94a3b8',
                        textAlign: 'center',
                      }}>
                        emails/day
                      </SizableText>
                    </View>
                  </XStack>
                </View>

                {/* Google Workspace */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" gap="$3" marginBottom={12}>
                    <View style={{
                      backgroundColor: theme ? "#d1fae5" : "#064e3b",
                      borderRadius: 10,
                      padding: 8,
                    }}>
                      <MaterialIcons
                        name="business"
                        size={24}
                        color={theme ? "#059669" : "#10b981"}
                      />
                    </View>
                    <YStack flex={1}>
                      <SizableText style={{
                        fontSize: 18,
                        fontWeight: '700',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginBottom: 2,
                      }}>
                        Google Workspace
                      </SizableText>
                      <SizableText style={{
                        fontSize: 12,
                        color: theme ? '#64748b' : '#94a3b8',
                      }}>
                        Business accounts with custom domain
                      </SizableText>
                    </YStack>
                    <View style={{
                      backgroundColor: theme ? "#d1fae5" : "#064e3b",
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                    }}>
                      <SizableText style={{
                        fontSize: 24,
                        fontWeight: '800',
                        color: theme ? "#059669" : "#10b981",
                      }}>
                        2,000
                      </SizableText>
                      <SizableText style={{
                        fontSize: 11,
                        color: theme ? '#64748b' : '#94a3b8',
                        textAlign: 'center',
                      }}>
                        emails/day
                      </SizableText>
                    </View>
                  </XStack>
                </View>

                {/* Microsoft 365/Outlook */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" gap="$3" marginBottom={12}>
                    <View style={{
                      backgroundColor: theme ? "#e0e7ff" : "#312e81",
                      borderRadius: 10,
                      padding: 8,
                    }}>
                      <MaterialIcons
                        name="email"
                        size={24}
                        color={theme ? "#4f46e5" : "#818cf8"}
                      />
                    </View>
                    <YStack flex={1}>
                      <SizableText style={{
                        fontSize: 18,
                        fontWeight: '700',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginBottom: 2,
                      }}>
                        Microsoft 365 / Outlook
                      </SizableText>
                      <SizableText style={{
                        fontSize: 12,
                        color: theme ? '#64748b' : '#94a3b8',
                      }}>
                        Office 365 and Outlook.com accounts
                      </SizableText>
                    </YStack>
                    <View style={{
                      backgroundColor: theme ? "#e0e7ff" : "#312e81",
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                    }}>
                      <SizableText style={{
                        fontSize: 24,
                        fontWeight: '800',
                        color: theme ? "#4f46e5" : "#818cf8",
                      }}>
                        10,000
                      </SizableText>
                      <SizableText style={{
                        fontSize: 11,
                        color: theme ? '#64748b' : '#94a3b8',
                        textAlign: 'center',
                      }}>
                        emails/day
                      </SizableText>
                    </View>
                  </XStack>
                </View>

                {/* Custom SMTP */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" gap="$3" marginBottom={12}>
                    <View style={{
                      backgroundColor: theme ? "#fef3c7" : "#451a03",
                      borderRadius: 10,
                      padding: 8,
                    }}>
                      <MaterialIcons
                        name="settings"
                        size={24}
                        color={theme ? "#f59e0b" : "#fbbf24"}
                      />
                    </View>
                    <YStack flex={1}>
                      <SizableText style={{
                        fontSize: 18,
                        fontWeight: '700',
                        color: theme ? '#1e293b' : '#f1f5f9',
                        marginBottom: 2,
                      }}>
                        Custom SMTP
                      </SizableText>
                      <SizableText style={{
                        fontSize: 12,
                        color: theme ? '#64748b' : '#94a3b8',
                      }}>
                        Depends on your SMTP provider settings
                      </SizableText>
                    </YStack>
                    <View style={{
                      backgroundColor: theme ? "#fef3c7" : "#451a03",
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                    }}>
                      <SizableText style={{
                        fontSize: 20,
                        fontWeight: '800',
                        color: theme ? "#f59e0b" : "#fbbf24",
                      }}>
                        Varies
                      </SizableText>
                      <SizableText style={{
                        fontSize: 11,
                        color: theme ? '#64748b' : '#94a3b8',
                        textAlign: 'center',
                      }}>
                        by provider
                      </SizableText>
                    </View>
                  </XStack>
                </View>

                {/* Info Note */}
                <View style={{
                  backgroundColor: theme ? '#eff6ff' : '#1e3a5f',
                  borderRadius: 12,
                  padding: 16,
                  borderWidth: 1,
                  borderColor: theme ? '#bfdbfe' : '#3b82f6',
                }}>
                  <XStack alignItems="flex-start" gap={12}>
                    <MaterialIcons
                      name="info-outline"
                      size={20}
                      color={theme ? '#3b82f6' : '#60a5fa'}
                      style={{ marginTop: 2 }}
                    />
                    <YStack flex={1} gap={4}>
                      <SizableText style={{
                        fontSize: 13,
                        fontWeight: '600',
                        color: theme ? '#1e40af' : '#93c5fd',
                      }}>
                        Important Notes:
                      </SizableText>
                      <SizableText style={{
                        fontSize: 12,
                        color: theme ? '#1e3a8a' : '#bfdbfe',
                        lineHeight: 18,
                      }}>
                        • Limits are per day and reset at midnight (provider's timezone){'\n'}
                        • Exceeding limits may result in temporary sending restrictions{'\n'}
                        • Custom SMTP limits depend on your provider's configuration{'\n'}
                        • Microsoft 365 limits vary by subscription plan
                      </SizableText>
                    </YStack>
                  </XStack>
                </View>
              </ScrollView>
            </View>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => setEmailLimitsModalOpen(false)}
                    style={{
                      paddingHorizontal: 24,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#3b82f6' : '#2563eb',
                      minWidth: 100,
                      alignItems: 'center',
                      elevation: 2,
                      shadowColor: '#3b82f6',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Got it
                    </Text>
                  </Pressable>
                </Dialog.Close>
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </DefaultScreen>
  )
}

export default MailLoginScreen;